import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Grid3X3, PlaySquare, AtSign, Bookmark } from 'lucide-react';
import type { Post, Reel, User } from '@/shared/types';
import { SmartImage } from '@/shared/ui/SmartImage';
import { cn } from '@/shared/utils/cn';

type Tab = 'posts' | 'reels' | 'tagged' | 'saved';

type Props = {
  user: User;
  posts: Post[];
  reels: Reel[];
  saved: Post[];
  isMe: boolean;
};

export function ProfileTabs({ user: _user, posts, reels, saved, isMe }: Props) {
  const [active, setActive] = useState<Tab>('posts');
  const tabs: { id: Tab; icon: ReactNode; show: boolean }[] = [
    { id: 'posts', icon: <Grid3X3 className="w-4 h-4" />, show: true },
    { id: 'reels', icon: <PlaySquare className="w-4 h-4" />, show: true },
    { id: 'tagged', icon: <AtSign className="w-4 h-4" />, show: true },
    { id: 'saved', icon: <Bookmark className="w-4 h-4" />, show: isMe },
  ];

  const items = active === 'reels' ? reels : active === 'saved' ? saved : posts;

  return (
    <section className="mt-6">
      <div className="grid grid-cols-4 border-y border-white/[0.08]">
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <button
              key={t.id}
              data-testid={`profile-tab-${t.id}`}
              data-active={active === t.id}
              onClick={() => setActive(t.id)}
              className={cn(
                'py-3 flex justify-center transition-colors',
                active === t.id ? 'text-white' : 'text-white/30',
              )}
            >
              {t.icon}
            </button>
          ))}
      </div>
      <div className="grid grid-cols-3 gap-[2px] mt-[2px]">
        {items.length === 0 && (
          <div className="col-span-3 text-center text-white/40 text-sm italic py-10">
            Nothing here yet.
          </div>
        )}
        {items.map((it) => {
          const href =
            'kind' in it ? `/instaflix/post/${it.id}` : `/reelsflix?focus=${it.id}`;
          const cover = 'media' in it ? it.media[0] : it.poster;
          return (
            <Link
              key={it.id}
              to={href}
              className="relative aspect-square overflow-hidden bg-black/30"
            >
              <SmartImage fill src={cover} alt="" sizes="33vw" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}
